import React from 'react';
import { FormPublicDesignFragment } from '../../../graphql/fragment/form.public.fragment';
import { StyledButton } from '@/components/styled/button';
import { Typography, Grid2, Box } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ListItemText from '@mui/material/ListItemText';
import Image from 'next/image';
import headingBg from './assets/gold-bubble.png';
import bigCluster from './assets/big-cluster.png';
import blackBubble from './assets/black-bubble.png';
import clearBubble from './assets/clear-bubble.png';
import goldBubble from './assets/gold-bubble.png';
import ballTrioLevels from './assets/ball-trio-levels.png';
import blackClearDots from './assets/black-clear-dots.png';
import turtle from './assets/turtle.svg';
import chart from './assets/chart.svg';
import map from './assets/map.svg';
import target from './assets/target.svg';
import { Footer } from './components/footer';
import { InstructionWrapper } from './components/instructionWrapper';
import InstructionsGrid from './components/instructionsGrid';
import LevelsTable from './components/levelsTable';

interface Props {
  design: FormPublicDesignFragment;
  next: () => void;
}

interface LevelLabels {
  experience: string[];
  aspiration: string[];
}

export interface Instruction {
  image: HTMLImageElement;
  title: string;
  text: string;
}

export interface LevelsTableRows {
  [key: string]: string[];
}

const levelLabels: LevelLabels = {
  experience: [
    //'Only you and your coach can see your results'
    'Only you and your coach can see your results',
    'This is a way to drive your career goals',
    'It is a baseline of where you are and where you want to be',
    'Design Leadership will only see a blended average of our full practice',
  ],
  aspiration: [
    //"It is not publicly visible",
    'It is not publicly visible',
    'It is not a reflection of your value or performance ',
    'It will not be used against practitioners and their skills or experience (at any level)',
  ],
};

const instructionText = [
  {
    title: 'RATE YOURSELF',
    text: 'Rate both your <b>experience level</b> and your <b>aspiration level</b>, according to the key below, on 6 sections: Universal Skills, Design Skills, Tools, Type of Engagements, Industries & Sectors, and Platforms.',
  },
  {
    title: 'LEARN ALONG THE WAY',
    text: 'The more <b>thorough and thoughtful</b> you are in your answers, the more accurate and helpful your results will be. To learn more about unfamiliar terms, click the highlighted term to get the <b>glossary definition</b>.',
  },
  {
    title: 'REVIEW AND SHARE YOUR RESULTS',
    text: 'Use the results data to inform your career aspirations. Let this be a springboard from which <b>you and your coach</b> set your professional goals, outline learning opportunities, and measure your progress over time.',
  },
];

const instructions: Instruction[] = [
  {
    image: turtle,
    title: 'Pace yourself',
    text: 'Give yourself <b>45 mins</b> to do this and be thoughtful about your answers',
  },
  {
    image: target,
    title: 'Learn the levels',
    text: 'Have a solid understanding of what the <b>0-5 levels</b> mean and how they are different',
  },
  {
    image: chart,
    title: 'Review results with your coach',
    text: 'Plan on spending <b>30 mins</b> talking about your answers and results with your coach',
  },
  {
    image: map,
    title: 'Take it twice a year',
    text: 'Remember, you’ll take this <b>twice a year</b>, in January and June',
  },
];

const levelsTableRows: LevelsTableRows = {
  'Experience Level': [
    '<i>What is your amount of experience and understanding?<i>',
    'No experience around this subject',
    'Understands the basics, able to solve subject-related problems with team or mentor assistance',
    'Adept in the subject, can independently apply the knowledge with minimal team or mentor assistance',
    'Understands the subject enough to independently apply the knowledge in solving project-related problems',
    'Fully understands the subject, able to take lead in applying the knowledge and direct others accordingly',
    'Considered a veteran in the subject, able to identify needs before presented with a problem, teach others and talk/write at length on the subject',
  ],
  'Aspiration to Learn and Do': [
    '<i>What is your interest level in doing this in the future?<i>',
    'No interest in the subject',
    'Understand the basics and would like to learn / read more',
    'Familiar with the subject, interested in learning more and getting more experience',
    'Understand the subject, would like to gain deeper learnings and ultimately become an expert',
    'Fully understand the subject, and would like to make this a focus of mine',
    'Considered a veteran in the subject, this is a focus of mine, and would like to teach the subject to others',
  ],
};

const levelTableHeaders = [
  'NOT APPLICABLE',
  'NOVICE',
  'INTERMEDIATE',
  'ADVANCED',
  'EXPERT',
  'MASTER',
];

export const DesignSkillStackStartPage: React.FC<Props> = ({
  design,
  next,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          zIndex: -1,
          top: '830px',
          left: '0px',
          width: '15%',
          display: { xs: 'none', md: 'block' },
          height: '350px',
          maxWidth: '300px',
          backgroundImage: `url(${blackClearDots})`,
          backgroundSize: 'cover',
        }}
      ></Box>
      <Box
        sx={{
          position: 'absolute',
          top: '600px',
          right: '0px',
          width: '53%',
          maxWidth: '575px',
          minHeight: '770px',
          display: { xs: 'none', md: 'block' },
          backgroundImage: `url(${bigCluster})`,
          backgroundSize: 'cover',
        }}
      ></Box>
      <Grid2
        sx={{
          marginTop: '113px',
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '1200px',
          padding: '0 30px 40px',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '113px',
            right: '100px',
            width: '38px',
            height: '35px',
            backgroundImage: `url(${headingBg})`,
            backgroundSize: 'cover',
          }}
        ></Box>
        <Typography
          sx={{ typography: { sm: 'h4', xs: 'h5' } }}
          style={{
            color: design.colors.question,
            fontFamily: 'Open Sans',
            fontSize: '50px',
            fontWeight: '400',
            paddingTop: '100px',
          }}
        >
          Hello,
          <br />
          Welcome to our{' '}
          <span
            style={{
              fontWeight: '800',
            }}
          >
            Design Skill Stack
          </span>
        </Typography>
        <Typography
          sx={{
            fontSize: '24px',
            color: design.colors.question,
            fontFamily: 'Open Sans',
            marginTop: '24px',
          }}
        >
          Take this inventory of skills to determine your experience, skills,
          and aspirations in specific areas of design to enable better
          discussions with your coach around your career goals.
        </Typography>
        <Typography
          sx={{
            fontSize: '24px',
            color: design.colors.question,
            fontFamily: 'Open Sans',
            marginTop: '24px',
          }}
        >
          Additionally, we’re using this understanding of our group’s skills and
          interests to help set our goals, measure our successes, staff better,
          and identify our collective strengths and gaps.
        </Typography>
        <StyledButton
          background={design.colors.button}
          $buttonColor={design.colors.buttonText}
          highlight={design.colors.buttonActive}
          type='submit'
          className={'continue-button'}
          onClick={() => next()}
          style={{
            marginTop: '40px',
            width: '226px',
            padding: '12px',
          }}
        >
          {'Start Your Skill Stack'}
          <ArrowForwardIcon
            sx={{ height: '20px', width: '15px', marginLeft: '8px' }}
          />
        </StyledButton>
        <Box
          sx={{
            position: 'absolute',
            top: '500px',
            left: '550px',
            width: '12%',
            display: { xs: 'none', md: 'block' },
            height: '10%',
            backgroundImage: `url(${blackBubble})`,
            backgroundSize: 'cover',
          }}
        ></Box>
        <Box
          sx={{
            position: 'absolute',
            zIndex: -3,
            top: '460px',
            left: '625px',
            width: '12%',
            display: { xs: 'none', md: 'block' },
            height: '10%',
            backgroundImage: `url(${clearBubble})`,
            backgroundSize: 'cover',
          }}
        ></Box>
        <Box
          sx={{
            position: 'absolute',
            zIndex: -2,
            top: '540px',
            left: '655px',
            width: '2%',
            display: { xs: 'none', md: 'block' },
            height: '1.5%',
            backgroundImage: `url(${goldBubble})`,
            backgroundSize: 'cover',
          }}
        ></Box>
        <Box maxWidth={'930px'}>
          <Typography
            sx={{ typography: { sm: 'h4', xs: 'h5' } }}
            style={{
              color: design.colors.question,
              fontFamily: 'Open Sans',
              fontSize: '24px',
              fontWeight: '400',
              paddingTop: '150px',
            }}
          >
            HOW IT WORKS
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              paddingTop: '24px',
              gap: '36px',
            }}
          >
            {instructionText.map((item, index) => (
              <InstructionWrapper
                key={index}
                index={index + 1}
                title={item.title}
                text={item.text}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography
              style={{
                color: design.colors.question,
                fontFamily: 'Open Sans',
                fontSize: '24px',
                fontWeight: '400',
                paddingTop: '75px',
              }}
            >
              BEFORE YOU BEGIN
            </Typography>
            <Typography
              style={{
                fontSize: '24px',
                marginTop: '15px',
                color: '#FFFFFFA3',
              }}
            >
              The Skill Stack is a tool meant to be completed by yourself,
              shared with your coach, and used as a discussion guide.
            </Typography>
            <InstructionsGrid
              design={design}
              instructions={instructions}
            ></InstructionsGrid>
          </Box>
        </Box>
      </Grid2>
      <Grid2
        container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <Grid2
          sx={{
            maxWidth: '1200px',
            padding: '0 30px 100px',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <Grid2>
            <Box
              display='flex'
              flexDirection='row'
              alignItems='center'
              gap='20px'
            ></Box>
          </Grid2>
        </Grid2>
      </Grid2>

      <Grid2
        container
        sx={{
          backgroundColor: '#ECF8FF',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Grid2
          sx={{
            padding: '70px 30px 90px 35px',
          }}
          size={{ xs: 10 }}
        >
          <Typography
            style={{
              color: '#000',
              fontSize: '40px',
              fontWeight: '700',
            }}
          >
            Levels of Experience{' '}
            <Typography
              style={{
                color: '#000',
                fontSize: '40px',
                fontWeight: '300',
                display: 'inline',
              }}
            >
              & Aspiration
            </Typography>
          </Typography>
          <Typography
            style={{
              color: '#000000A3',
              fontSize: '24px',
              fontWeight: '400',
            }}
          >
            What is your experience level in each subject? And what is your
            interest level in doing this in the future?
          </Typography>
          <LevelsTable
            levelsTableRows={levelsTableRows}
            levelsTableHeaders={levelTableHeaders}
          ></LevelsTable>
          <StyledButton
            background={design.colors.button}
            $buttonColor={design.colors.buttonText}
            highlight={design.colors.buttonActive}
            type='submit'
            className={'continue-button'}
            onClick={() => next()}
            style={{
              marginTop: '40px',
              width: '220px',
            }}
          >
            {'Start Your Skill Stack'}
            <ArrowForwardIcon
              sx={{ height: '20px', width: '15px', marginLeft: '8px' }}
            />
          </StyledButton>
        </Grid2>
      </Grid2>
      <Grid2
        container
        sx={{
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            zIndex: -1,
            bottom: '1230px',
            right: '100px',
            width: '12%',
            maxWidth: '180px',
            height: '250px',
            display: { xs: 'none', md: 'block' },
            backgroundImage: `url(${ballTrioLevels})`,
            backgroundSize: 'cover',
          }}
        ></Box>
        <Grid2 sx={{ maxWidth: '1200px', padding: '0 30px', width: '100%' }}>
          <Typography
            style={{
              color: design.colors.question,
              fontSize: '40px',
              marginTop: '100px',
              fontWeight: '300',
            }}
          >
            <span
              style={{
                fontWeight: '700',
              }}
            >
              What this is
            </span>{' '}
            and what it isn't
          </Typography>

          <Typography
            style={{
              color: 'rgba(255, 255, 255, 0.64)',
              fontSize: '24px',
              marginTop: '15px',
            }}
          >
            The Design Skill Stack is a discussion guide for all design
            practitioners within Design-led Product & Engineering (DP&E). It is{' '}
            <span
              style={{
                color: design.colors.question,
                fontWeight: '400',
              }}
            >
              not
            </span>{' '}
            another performance assessment framework.
          </Typography>

          <Grid2
            sx={{
              flexDirection: 'row',
              display: 'flex',
              marginTop: '50px',
            }}
          >
            <Grid2 size={{ xs: 7 }}>
              <Typography
                style={{
                  color: design.colors.question,
                  fontSize: '20px',
                  fontWeight: '700',
                }}
              >
                This is a discussion guide for you and your coach
              </Typography>
              <List sx={{ color: design.colors.question }}>
                {levelLabels.experience.map((labelText: string, i: number) => (
                  <ListItem key={i} disablePadding>
                    <ListItemIcon>
                      <Image
                        src='/green-check.svg'
                        alt='checkmark'
                        width={36}
                        height={40}
                      />
                    </ListItemIcon>
                    <ListItemText primary={labelText} />
                  </ListItem>
                ))}
              </List>
            </Grid2>
            <Grid2 size={{ xs: 5 }}>
              <Typography
                style={{
                  color: design.colors.question,
                  fontSize: '20px',
                  fontWeight: '700',
                }}
              >
                This is not for performance measurement
              </Typography>
              <List sx={{ color: design.colors.question }}>
                {levelLabels.aspiration.map((labelText: string, i: number) => (
                  <ListItem key={i} disablePadding>
                    <ListItemIcon>
                      <Image src='/red-x.svg' alt='x' width={36} height={40} />
                    </ListItemIcon>
                    <ListItemText primary={labelText} />
                  </ListItem>
                ))}
              </List>
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>
      <Footer design={design} />
    </div>
  );
};
